/**
 * PATH: packages/ui/src/components/settings/security-tab/totp.ts
 * PURPOSE: Client-side TOTP (RFC 6238, SHA-1/6-digit/30s) + per-device
 *          secret storage — powers the built-in authenticator.
 * WHY: Client ask (2026-07): "Have MFA built into the apps as an option
 *      to generate tokens here.. and copy paste them once it is
 *      registered to a device". Registering = saving the enrolment
 *      setup key on this device; the app then computes the same codes
 *      an external authenticator would.
 * FLOW:
 *   ┌─────────────┐   ┌──────────────┐   ┌─────────────────┐
 *   │ base32 key  │ → │ HMAC-SHA1 of │ → │ 6-digit code +  │
 *   │ (localStore)│   │ time counter │   │ seconds left    │
 *   └─────────────┘   └──────────────┘   └─────────────────┘
 * DEPENDENCIES:
 *   - Web Crypto (crypto.subtle): HMAC — available in all target
 *     browsers and jsdom/node test envs.
 */

const B32_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567'
const STORAGE_PREFIX = 'quotech_totp_key'
const PERIOD_SECONDS = 30
const DIGITS = 6

/** Decode an RFC 4648 base32 string (case/space/padding tolerant). */
export function base32Decode(input: string): Uint8Array {
  const clean = input.toUpperCase().replace(/[\s=-]/g, '')
  let bits = 0
  let value = 0
  const out: number[] = []
  for (const ch of clean) {
    const idx = B32_ALPHABET.indexOf(ch)
    if (idx === -1) throw new Error(`Invalid base32 character: ${ch}`)
    value = (value << 5) | idx
    bits += 5
    if (bits >= 8) {
      out.push((value >>> (bits - 8)) & 0xff)
      bits -= 8
    }
  }
  return new Uint8Array(out)
}

/** True if the string decodes to a plausible TOTP secret (>= 10 bytes). */
export function isValidTotpSecret(secret: string): boolean {
  try {
    return base32Decode(secret).length >= 10
  } catch {
    return false
  }
}

export interface TotpCode {
  code: string
  /** Seconds until this code rotates. */
  secondsRemaining: number
}

/** Compute the current TOTP code for a base32 secret. */
export async function generateTotpCode(
  secret: string,
  nowMs: number = Date.now(),
): Promise<TotpCode> {
  const keyBytes = base32Decode(secret)
  const counter = Math.floor(nowMs / 1000 / PERIOD_SECONDS)
  const counterBuf = new ArrayBuffer(8)
  const view = new DataView(counterBuf)
  // JS bitwise ops are 32-bit; split the 64-bit counter manually.
  view.setUint32(0, Math.floor(counter / 0x100000000))
  view.setUint32(4, counter % 0x100000000)

  const key = await crypto.subtle.importKey(
    'raw',
    keyBytes.buffer.slice(keyBytes.byteOffset, keyBytes.byteOffset + keyBytes.byteLength) as ArrayBuffer,
    { name: 'HMAC', hash: 'SHA-1' },
    false,
    ['sign'],
  )
  const hmac = new Uint8Array(await crypto.subtle.sign('HMAC', key, counterBuf))
  const offset = hmac[hmac.length - 1] & 0x0f
  const binary =
    ((hmac[offset] & 0x7f) << 24) |
    (hmac[offset + 1] << 16) |
    (hmac[offset + 2] << 8) |
    hmac[offset + 3]
  const code = String(binary % 10 ** DIGITS).padStart(DIGITS, '0')
  const secondsRemaining = PERIOD_SECONDS - (Math.floor(nowMs / 1000) % PERIOD_SECONDS)
  return { code, secondsRemaining }
}

/* ───── Per-device secret storage ("registered to this device") ───── */

function storageKey(userId?: string): string {
  return userId ? `${STORAGE_PREFIX}:${userId}` : STORAGE_PREFIX
}

export function getStoredTotpSecret(userId?: string): string | null {
  try {
    return window.localStorage.getItem(storageKey(userId))
  } catch {
    return null
  }
}

export function storeTotpSecret(secret: string, userId?: string): void {
  window.localStorage.setItem(storageKey(userId), secret.toUpperCase().replace(/[\s=-]/g, ''))
}

export function removeTotpSecret(userId?: string): void {
  try {
    window.localStorage.removeItem(storageKey(userId))
  } catch {
    /* storage unavailable — nothing to remove */
  }
}
